import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import Btn from '../../../../UI/Btn'
import http from '../../../../shared/http'
import { TApi } from '../../../../shared/const'
import { hygieneAppliesToBranch } from '../../../../shared/hygieneTraining'

import styles from './style.module.scss'
import { formatVienna } from '../../../../shared/datetime'

/*
 * Lebensmittelhygiene und Allergeninformation (Anforderung #96).
 *
 * Steht unter dem Feld „Branche" im Unternehmensprofil und erscheint nur, wenn
 * dort eine sensible Branche gewählt ist (Gesundheit & Schönheit,
 * Nahrungsmittel & Getränke — die Liste steht in den Einstellungen).
 *
 * Drei Rollen, ein Baustein:
 *   Schüler:innen  wählen einen der beiden Wege und bestätigen.
 *   Lehrkraft      sieht die Angabe und kann ihr widersprechen.
 *   Verwaltung     liest mit.
 * Wer was darf, sagt der Server (`canConfirm`, `canObject`) — die Knöpfe hier
 * sind Anzeige, nicht Schutz.
 *
 * Seit Anforderung #183 trägt „Im Lehrplan integriert" keine Zusage mehr, die
 * Schulung nachzuholen, und es gibt dazu auch keine Erinnerung. Der Zeitpunkt
 * im Text ist überall derselbe: vor Beginn der Herstellung.
 *
 * Der Hinweistext ist bewusst zurückhaltend formuliert: Die Anwendung nimmt
 * eine Selbstauskunft entgegen, sie prüft nichts, bescheinigt nichts und nennt
 * keine Rechtsnormen. Verantwortlich bleiben Schule, Schulerhalter und
 * betreuende Lehrkraft — das steht im Absatz und darf beim Übersetzen nicht
 * verloren gehen.
 */

const STATUS = { CURRICULUM: 'CURRICULUM', COMPLETED: 'COMPLETED' }

// Kurse und Informationen zur Hygiene- und Allergenschulung (Anforderung #183).
const LFI_URL = 'https://oe.lfi.at/'

const zeitpunkt = wert => {
	if (!wert) return ''
	try {
		return formatVienna(wert, 'dd.MM.yyyy, HH:mm')
	} catch {
		return ''
	}
}

const tag = wert => {
	if (!wert) return ''
	try {
		return formatVienna(String(wert).slice(0, 10), 'dd.MM.yyyy')
	} catch {
		return String(wert)
	}
}

function HygieneTraining(props) {
	const { t } = useTranslation()

	const [state, setState] = useState(null)
	const [loading, setLoading] = useState(true)
	const [saving, setSaving] = useState(false)
	const [expanded, setExpanded] = useState(false)

	// Auswahl in Bearbeitung. Sie startet auf dem gespeicherten Stand und darf
	// davon abweichen, bis bestätigt wird — deshalb ein eigener Zustand.
	const [choice, setChoice] = useState(null)
	const [people, setPeople] = useState([])
	const [trainingDate, setTrainingDate] = useState('')
	const [reason, setReason] = useState('')

	const { companyId } = props

	const load = useCallback(() => {
		if (!companyId) return

		setLoading(true)
		http.get(TApi.COMMON_COMPANY_HYGIENE(companyId))
			.then(({ data }) => {
				setState(data)
				setChoice(data.confirmation?.status || null)
				setPeople((data.confirmation?.participants || []).map(person => person.userId).filter(Boolean))
				setTrainingDate(
					data.confirmation?.trainingDate ? String(data.confirmation.trainingDate).slice(0, 10) : ''
				)
			})
			.catch(() => setState(null))
			.finally(() => setLoading(false))
	}, [companyId])

	useEffect(() => {
		load()
	}, [load, props.savedBranch])

	/*
	 * Die gewählte Branche zählt sofort — auch bevor sie gespeichert ist: Wer
	 * „Nahrungsmittel & Getränke" auswählt, soll den Hinweis lesen, ohne vorher
	 * speichern zu müssen. Bestätigen lässt sich erst danach; darauf weist der
	 * Satz unten hin.
	 */
	const giltHier = hygieneAppliesToBranch(props.options, props.branch)
	const gespeichertGilt = state?.applies === true

	if (!giltHier && !gespeichertGilt) return null

	const confirmation = state?.confirmation || null
	const widersprochen = !!confirmation?.objectedAt
	const bestaetigt = !!confirmation && !widersprochen

	const darfBestaetigen = !props.readOnly && state?.canConfirm
	const darfWidersprechen = !props.readOnly && state?.canObject && bestaetigt

	// „Abgeschlossen" ist keine Angabe, die sich zurücknehmen lässt — der Server
	// weist den Weg zurück ab, und die Oberfläche bietet ihn gar nicht erst an.
	const schulungAbgeschlossen = confirmation?.status === STATUS.COMPLETED && !widersprochen

	const geaendert =
		choice !== (confirmation?.status || null) ||
		(choice === STATUS.COMPLETED &&
			(people.join(',') !== (confirmation?.participants || []).map(p => p.userId).join(',') ||
				trainingDate !== (confirmation?.trainingDate ? String(confirmation.trainingDate).slice(0, 10) : '')))

	const togglePerson = id =>
		setPeople(current => (current.includes(id) ? current.filter(entry => entry !== id) : [...current, id]))

	const confirm = () => {
		if (!choice) return toast.error(t('Please select one of the two statements.'))

		setSaving(true)
		http.put(TApi.COMMON_COMPANY_HYGIENE(companyId), {
			status: choice,
			participants: choice === STATUS.COMPLETED ? people : [],
			trainingDate: choice === STATUS.COMPLETED && trainingDate ? trainingDate : null
		})
			.then(() => {
				toast.success(t('Confirmation saved.'))
				load()
				props.onChanged?.()
			})
			.finally(() => setSaving(false))
	}

	const object = () => {
		if (reason.trim().length < 3) return toast.error(t('Please state briefly why the confirmation is not correct.'))

		setSaving(true)
		http.put(TApi.COMMON_COMPANY_HYGIENE_OBJECTION(companyId), { reason: reason.trim() })
			.then(() => {
				toast.success(t('Objection saved.'))
				setReason('')
				load()
				props.onChanged?.()
			})
			.finally(() => setSaving(false))
	}

	const auswahl = (value, title, hint) => (
		<label className={clsx(styles.choice, choice === value && styles.choiceActive)} key={value}>
			<span className={styles.choiceHead}>
				<input
					type="radio"
					name={`hygiene-${companyId}`}
					checked={choice === value}
					disabled={saving || (value === STATUS.CURRICULUM && schulungAbgeschlossen)}
					onChange={() => setChoice(value)}
				/>
				{title}
			</span>
			{hint && <span className={styles.choiceHint}>{hint}</span>}
		</label>
	)

	return (
		<div className={styles.root}>
			<h3 className={styles.title}>{t('Note on food hygiene and allergen information')}</h3>

			<p className={styles.text}>
				{t(
					'Statutory requirements apply to the production, processing, sale and labelling of food as well as products and services applied to the body. If the hygiene and allergen training is not covered in the curriculum, it must be completed before production begins. All information and the courses can be found at'
				)}{' '}
				<a className={styles.link} href={LFI_URL} target="_blank" rel="noopener noreferrer">
					{LFI_URL}
				</a>
			</p>

			{expanded && (
				<>
					<p className={styles.text}>
						{t(
							'These requirements apply regardless of whether the sale is made for payment, within the school context or only occasionally. Please clarify with your teacher before production begins how these contents are covered in your Junior Company, and confirm this below.'
						)}
					</p>
					<p className={styles.legal}>
						{t(
							'The information is a self-declaration of the Junior Company. Junior Achievement Austria only provides the platform for documentation, does not carry out any review and does not give legal advice. The school, the school authority and the supervising teacher are responsible for complying with the applicable regulations. This entry does not replace any officially required certificates or approvals.'
						)}
					</p>
				</>
			)}

			<button type="button" className={styles.more} onClick={() => setExpanded(current => !current)}>
				{expanded ? t('Show less') : t('Read more')}
			</button>

			{loading && <p className={styles.text}>{t('Loading')}…</p>}

			{!loading && (
				<>
					{/* Der gültige Stand steht über der Auswahl: erst was gilt, dann was zu tun ist. */}
					{confirmation && (
						<div
							className={clsx(
								styles.state,
								widersprochen && styles.stateObjected,
								!widersprochen && confirmation.status === STATUS.CURRICULUM && styles.stateOpen
							)}
						>
							{widersprochen ? (
								<>
									<strong>{t('The supervising teacher has objected to this confirmation.')}</strong>
									{confirmation.objectionReason && <div>{confirmation.objectionReason}</div>}
									<span className={styles.stateMeta}>
										{confirmation.objectedByName} · {zeitpunkt(confirmation.objectedAt)}
									</span>
								</>
							) : (
								<>
									<strong>
										{confirmation.status === STATUS.COMPLETED
											? t('Food hygiene and allergen training completed.')
											: t('Contents are integrated into the curriculum.')}
									</strong>
									{!!confirmation.participants?.length && (
										<div>
											{t('Participants')}:{' '}
											{confirmation.participants.map(person => person.name).join(', ')}
										</div>
									)}
									{confirmation.trainingDate && (
										<div>
											{t('Date of training')}: {tag(confirmation.trainingDate)}
										</div>
									)}
									<span className={styles.stateMeta}>
										{t('Confirmed by')} {confirmation.confirmedByName} ·{' '}
										{zeitpunkt(confirmation.confirmedAt)}
									</span>
								</>
							)}
						</div>
					)}

					{darfBestaetigen && (
						<>
							{auswahl(
								STATUS.CURRICULUM,
								t(
									'The contents on food hygiene and allergen information are integrated into the curriculum.'
								)
							)}
							{auswahl(
								STATUS.COMPLETED,
								t('Food hygiene and allergen training completed.'),
								t('Please select the employees who completed the training.')
							)}

							{choice === STATUS.COMPLETED && (
								<div className={styles.details}>
									<span className={styles.detailsLabel}>{t('Participants')}</span>
									<div className={styles.people}>
										{(state?.employees || []).map(person => (
											<button
												type="button"
												key={person.id}
												className={clsx(
													styles.person,
													people.includes(person.id) && styles.personOn
												)}
												onClick={() => togglePerson(person.id)}
											>
												{person.name}
											</button>
										))}
										{!state?.employees?.length && (
											<span className={styles.choiceHint}>{t('No employees yet.')}</span>
										)}
									</div>

									<span className={styles.detailsLabel}>{t('Date of training')}</span>
									<input
										type="date"
										className={styles.date}
										value={trainingDate}
										onChange={event => setTrainingDate(event.target.value)}
									/>
								</div>
							)}

							<div className={styles.actions}>
								<Btn
									loading={saving}
									disabled={!choice || !gespeichertGilt}
									attention={geaendert && !!choice}
									icon="arrow"
									iconSize={{ w: 10, h: 10 }}
									iconColor="#fff"
									onClick={confirm}
								>
									{confirmation ? t('Confirm again') : t('Confirm')}
								</Btn>
							</div>

							{!gespeichertGilt && (
								<p className={styles.hint}>
									{t('Please save the branch first — the confirmation belongs to the saved branch.')}
								</p>
							)}

							{schulungAbgeschlossen && (
								<p className={styles.hint}>{t('A completed training cannot be withdrawn.')}</p>
							)}
						</>
					)}

					{darfWidersprechen && (
						<div className={styles.objection}>
							<span className={styles.detailsLabel}>{t('Object to this confirmation')}</span>
							<textarea
								className={styles.objectionField}
								value={reason}
								placeholder={t('Please state briefly why the confirmation is not correct.')}
								onChange={event => setReason(event.target.value)}
							/>
							<div className={styles.actions}>
								<Btn theme="red" loading={saving} onClick={object}>
									{t('Object')}
								</Btn>
							</div>
						</div>
					)}

					{/* Ohne Bestätigung und ohne Recht dazu bleibt der Satz, was fehlt. */}
					{!confirmation && !darfBestaetigen && (
						<p className={styles.hint}>{t('No confirmation has been submitted yet.')}</p>
					)}
				</>
			)}
		</div>
	)
}

HygieneTraining.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	// Die gerade gewählte Branche (auch ungespeichert) und die gespeicherte.
	branch: PropTypes.string,
	savedBranch: PropTypes.string,
	readOnly: PropTypes.bool,
	onChanged: PropTypes.func,
	options: PropTypes.object
}

HygieneTraining.defaultProps = {
	readOnly: false
}

const mapStateToProps = state => ({
	options: state.options
})

export default connect(mapStateToProps)(HygieneTraining)
